import { useEffect } from 'react';
import { WidgetsSdk } from '@givebutter/widgets-core';
import {
  GivebutterButton,
  GivebutterGivingForm,
  GivebutterGoalBar,
  GivebutterWidget,
} from '@givebutter/widgets-react';
import { WidgetType } from 'lib/gb-api';

interface WidgetPreviewProps {
  type: WidgetType;
  accountAid: string;
  campaignCode?: string;
  config: Record<string, unknown>;
  savedDisplayId?: string;
}

const NUMERIC_KEYS = new Set(['borderRadius', 'borderWidth', 'openDelay', 'padding']);

const UNMAPPED_KEYS: Partial<Record<WidgetType, Set<string>>> = {
  button: new Set(['type']),
};

const toAttrs = (
  type: WidgetType,
  config: Record<string, unknown>,
): Record<string, string | boolean | number> => {
  const out: Record<string, string | boolean | number> = {};
  for (const [key, value] of Object.entries(config)) {
    if (value === null || value === undefined) continue;
    if (key === 'campaign') continue;
    if (UNMAPPED_KEYS[type]?.has(key)) continue;
    if (NUMERIC_KEYS.has(key)) {
      if (value === '') continue;
      const numeric = Number(value);
      if (Number.isFinite(numeric)) {
        out[key] = numeric;
      }
      continue;
    }
    if (typeof value === 'string' || typeof value === 'boolean' || typeof value === 'number') {
      out[key] = value;
    }
  }
  return out;
};

export const WidgetPreview = ({
  type,
  accountAid,
  campaignCode,
  config,
  savedDisplayId,
}: WidgetPreviewProps) => {
  useEffect(() => {
    const apiUrl = import.meta.env.VITE_WIX_APP_GB_API_URL;
    if (apiUrl) {
      WidgetsSdk.apiUrl = apiUrl;
    }
    if (accountAid) {
      WidgetsSdk.aid = accountAid;
    }
  }, [accountAid]);

  const requiresCampaign = type !== 'signup-form';
  const missingPrereq = !accountAid || (requiresCampaign && !campaignCode);

  if (missingPrereq) {
    return (
      <div className="flex h-full min-h-[300px] items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-sm text-gray-400">
        {requiresCampaign ? 'Pick a campaign to see the preview' : 'Loading preview…'}
      </div>
    );
  }

  const attrs = toAttrs(type, config);

  return (
    <div className="relative min-h-[150px] overflow-hidden rounded-lg border border-gray-200 bg-white p-6 contain-[layout]">
      <div className="pointer-events-none">
        {type === 'button' && (
          <GivebutterButton account={accountAid} campaign={campaignCode} {...attrs} />
        )}
        {type === 'giving-form' && (
          <GivebutterGivingForm account={accountAid} campaign={campaignCode} {...attrs} />
        )}
        {type === 'goal-bar' && (
          <GivebutterGoalBar account={accountAid} campaign={campaignCode} {...attrs} />
        )}
        {type === 'signup-form' &&
          (savedDisplayId ? (
            <GivebutterWidget account={accountAid} id={savedDisplayId} />
          ) : (
            <p className="text-sm text-gray-400">
              Save the signup form to see the rendered preview
            </p>
          ))}
      </div>
    </div>
  );
};
